import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Eye, EyeOff, Mail, Lock, User, Phone, Shield, ArrowRight, CheckCircle, Users, Truck, Building2 } from 'lucide-react';
import { authService } from '../services';
import CountryCodeSelector from '../components/ui/CountryCodeSelector';
import { getUrlForDashboard, getExpectedDashboardForRole, getCurrentPort, getPortForDashboard } from '../utils/portUtils';
import { getDashboardRoute } from '../utils/roleNavigation';

// Registration Page - Route: /register
// Note: The sign up link has been removed from the login page, but this route is still accessible via direct URL navigation
const RegistrationPage = ({ dashboardType = 'admin' }) => {
  const { register, isLoading: authLoading, error: authError, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    phone_country_code: '+91', // Default to India
    role: '',
    agreeToTerms: false,
    contactInfo: {
      address: '',
      city: '',
      state: '',
      pincode: ''
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      console.log('🔒 User already authenticated, redirecting to dashboard...');
      // Redirect based on user role using port-based routing
      const expectedDashboard = getExpectedDashboardForRole(user.role);
      const currentPort = getCurrentPort();
      const expectedPort = getPortForDashboard(expectedDashboard);
      
      // Only redirect if not already on the correct port
      if (currentPort !== expectedPort) {
        const redirectUrl = getUrlForDashboard(expectedDashboard, '/dashboard');
        console.log(`[RegistrationPage] Redirecting ${user.role} from port ${currentPort} to ${expectedDashboard} dashboard (port ${expectedPort}): ${redirectUrl}`);
        window.location.href = redirectUrl;
      } else {
        console.log(`[RegistrationPage] User ${user.role} already on correct port ${currentPort}, navigating to dashboard route`);
        const dashboardRoute = getDashboardRoute(user.role);
        navigate(dashboardRoute, { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Get dashboard-specific branding
  const getDashboardBranding = () => {
    switch (dashboardType) {
      case 'admin':
        return {
          primaryColor: 'bg-red-600',
          primaryColorHover: 'hover:bg-red-700',
          primaryColorText: 'text-red-600',
          primaryColorBorder: 'border-red-300',
          primaryColorRing: 'focus:ring-red-500',
          primaryColorBg: 'bg-red-600'
        };
      case 'hospital':
        return {
          primaryColor: 'bg-blue-600',
          primaryColorHover: 'hover:bg-blue-700',
          primaryColorText: 'text-blue-600',
          primaryColorBorder: 'border-blue-300',
          primaryColorRing: 'focus:ring-blue-500',
          primaryColorBg: 'bg-blue-600'
        };
      case 'fleet':
        return {
          primaryColor: 'bg-green-600',
          primaryColorHover: 'hover:bg-green-700',
          primaryColorText: 'text-green-600',
          primaryColorBorder: 'border-green-300',
          primaryColorRing: 'focus:ring-green-500',
          primaryColorBg: 'bg-green-600'
        };
      case 'commandCenter':
        return {
          primaryColor: 'bg-purple-600',
          primaryColorHover: 'hover:bg-purple-700',
          primaryColorText: 'text-purple-600',
          primaryColorBorder: 'border-purple-300',
          primaryColorRing: 'focus:ring-purple-500',
          primaryColorBg: 'bg-purple-600'
        };
      default:
        return {
          primaryColor: 'bg-[#1e3a8a]',
          primaryColorHover: 'hover:bg-[#1e3a8a]/90',
          primaryColorText: 'text-[#1e3a8a]',
          primaryColorBorder: 'border-blue-300',
          primaryColorRing: 'focus:ring-blue-500',
          primaryColorBg: 'bg-[#1e3a8a]'
        };
    }
  };

  const branding = getDashboardBranding();

  const roles = [
    { value: 'SUPER_ADMIN', label: 'Super Admin', description: 'System administrator with full access' },
    { value: 'SUPER_ADMIN_USER', label: 'Super Admin User', description: 'Super admin user (web platform)' },
    { value: 'TENANT_ADMIN', label: 'Tenant Admin', description: 'Fleet operator administrator' },
    { value: 'HOSPITAL_USER', label: 'Hospital User', description: 'Hospital staff' },
    { value: 'TENANT_USER', label: 'Tenant User', description: 'Fleet operator user' },
    { value: 'DRIVER', label: 'Driver', description: 'Ambulance driver' },
    { value: 'INDIVIDUAL_USER', label: 'Individual User', description: 'End customer' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Phone number validation
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Role validation
    if (!formData.role) {
      newErrors.role = 'Please select a role';
    }
    
    // Terms agreement validation
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Prepare data for OTP registration (only fields expected by otpRegistrationSchema)
      const otpRegistrationData = {
        email: formData.email,
        phone: formData.phone,
        role: formData.role
      };
      
      // Call backend register-with-otp endpoint
      const result = await authService.registerWithOtp(otpRegistrationData);

      if (result.success) {
        const otpCode = result.data?.otp || result.data?.otpCode;
        console.log('✅ [RegistrationPage] OTP sent successfully. OTP for development:', otpCode);

        // Navigate to OTP page with email, phone, and form data
        navigate('/otp', {
          state: {
            email: formData.email,
            phone: formData.phone,
            formData: formData, // Keep full formData for completion step
            type: 'registration',
            otp: otpCode // Pass OTP for development mode
          }
        });
      }
    } catch (error) {
      console.error('Failed to register:', error);
      setErrors({ general: error.message || 'Failed to send verification code. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (roleValue) => {
    switch (roleValue) {
      case 'SUPER_ADMIN':
        return Shield;
      case 'SUPER_ADMIN_USER':
        return Shield;
      case 'TENANT_ADMIN':
        return Truck;
      case 'HOSPITAL_USER':
        return Building2;
      case 'TENANT_USER':
        return Users;
      case 'DRIVER':
        return Users;
      case 'INDIVIDUAL_USER':
        return Users;
      default:
        return Users;
    }
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Secure Platform',
      description: 'Enterprise-grade security with encrypted data transmission'
    },
    {
      icon: Users,
      title: 'Role-Based Access',
      description: 'Different access levels for admins, operators, and drivers'
    },
    {
      icon: Truck,
      title: 'Real-time Operations',
      description: 'Live tracking and instant communication across all users'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      
          {/* Right Side - Registration Form */}
          <div className="w-full max-w-md mx-auto lg:max-w-4xl">
            <Card className="shadow-xl rounded-xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="space-y-2 text-center pb-8">
                {/* <div className="flex items-center justify-center space-x-3 mb-4 lg:hidden">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#1e3a8a', border: '2px solid white'}}>
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <h1 className="text-xl font-bold text-gray-900">Smart Ambulance</h1>
                </div> */}
                <CardTitle className="text-2xl font-bold text-gray-900">Create Account</CardTitle>
                <CardDescription className="text-gray-600">
                  Join our platform to get started
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-600">{errors.general}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3">
                  {/* Full Name Field */}
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter Your Full Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`pl-12 sm:pl-14 ${errors.name ? 'border-red-300 focus:ring-red-500' : ''}`}
                        disabled={isLoading}
                      />
                    </div>
                    {errors.name && (
                      <p className="text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  {/* Email and Phone Fields in Same Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Email Field */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                          placeholder="Enter Your Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`pl-12 sm:pl-14 ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                        disabled={isLoading}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone Number Field */}
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <div className="flex">
                        <div className="w-16">
                        <CountryCodeSelector
                          value={formData.phone_country_code}
                          onChange={(e) => handleInputChange({ target: { name: 'phone_country_code', value: e.target.value } })}
                          disabled={isLoading}
                          placeholder="Country"
                        />
                      </div>
                        <div className="flex-1 relative pl-2">
                          <Phone className="absolute left-7 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                            placeholder="Enter Your Phone NO."
                          value={formData.phone}
                          onChange={handleInputChange}
                            className={`!pl-14 ${errors.phone ? 'border-red-300 focus:ring-red-500' : ''}`}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    {errors.phone && (
                      <p className="text-sm text-red-600">{errors.phone}</p>
                    )}
                    </div>
                  </div>

                  {/* Role Selection */}
                  <div className="space-y-2">
                    <label htmlFor="role" className="text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className={`w-full h-9 sm:h-10 rounded-md border px-3 py-2 text-xs sm:text-sm bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        errors.role ? 'border-red-300 focus:ring-red-500' : 'border-input'
                      }`}
                      disabled={isLoading}
                    >
                      <option value="">Select your role</option>
                      {roles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                    {errors.role && (
                      <p className="text-sm text-red-600">{errors.role}</p>
                    )}
                    {formData.role && (
                      <div className={`mt-2 p-3 ${branding.primaryColorBg} rounded-lg`}>
                        <div className="flex items-center space-x-2">
                          {React.createElement(getRoleIcon(formData.role), { className: "w-4 h-4 text-white" })}
                          <span className="text-sm font-medium text-white">
                            {roles.find(r => r.value === formData.role)?.label}
                          </span>
                        </div>
                        <p className="text-xs text-white/90 mt-1">
                          {roles.find(r => r.value === formData.role)?.description}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a Strong Password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={`pl-12 sm:pl-14 pr-12 sm:pr-14 ${errors.password ? 'border-red-300 focus:ring-red-500' : ''}`}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 sm:right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isLoading}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-sm text-red-600">{errors.password}</p>
                    )}
                    <div className="text-xs text-gray-500">
                      Password must contain at least 8 characters with uppercase, lowercase, and number
                    </div>
                  </div>

                  {/* Confirm Password Field */}
                  <div className="space-y-2">
                    <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm Your Password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={`pl-12 sm:pl-14 pr-12 sm:pr-14 ${errors.confirmPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 sm:right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isLoading}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-sm text-red-600">{errors.confirmPassword}</p>
                    )}
                  </div>

                  {/* Terms Agreement */}
                  <div className="space-y-2">
                    <div className="flex items-start space-x-2">
                      <input
                        id="agreeToTerms"
                        name="agreeToTerms"
                        type="checkbox"
                        checked={formData.agreeToTerms}
                        onChange={handleInputChange}
                        className={`w-4 h-4 mt-0.5 text-green-600 border-gray-300 rounded focus:ring-green-500 ${
                          errors.agreeToTerms ? 'border-red-300' : ''
                        }`}
                        disabled={isLoading}
                      />
                      <label htmlFor="agreeToTerms" className="text-sm text-gray-600">
                        I agree to the{' '}
                        <button type="button" className={`text-[#1e3a8a] hover:opacity-80 font-medium`}>
                          Terms of Service
                        </button>{' '}
                        and{' '}
                        <button type="button" className={`text-[#1e3a8a] hover:opacity-80 font-medium`}>
                          Privacy Policy
                        </button>
                      </label>
                    </div>
                    {errors.agreeToTerms && (
                      <p className="text-sm text-red-600">{errors.agreeToTerms}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className={`w-full ${branding.primaryColor} ${branding.primaryColorHover} text-white font-medium py-3 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending Verification Code...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>Send Verification Code</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    )}
                  </Button>
                </form>

                {/* Divider */}
                {/* <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div> */}

                {/* Social Registration Buttons */}
                {/* <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="w-full py-3 border-gray-200 hover:bg-gray-50 transition-colors"
                    disabled={isLoading}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full py-3 border-gray-200 hover:bg-gray-50 transition-colors"
                    disabled={isLoading}
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                    </svg>
                    Twitter
                  </Button>
                </div> */}

                {/* Sign In Link */}
                <div className="text-center pt-4">
                  <p className="text-sm text-gray-600">
                    Already have an account?{' '}
                    <button 
                      onClick={() => navigate('/login')}
                      className={`text-[#1e3a8a] hover:opacity-80 font-medium transition-colors`}
                    >
                      Sign in here
                    </button>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
   
  
  );
};

export default RegistrationPage;
